/**
 * AccessRequestPopup — the founder's one-click unblock for a blocked agent
 * (migration 0104 / access-request loop).
 *
 * When a scoped AGENT hits an access wall (a card read/write it isn't granted),
 * the gateway emits a Needs-You "access request" item. The founder opens it from
 * the Inbox deep-link (`?accessRequest=<id>` → the card route auto-opens this
 * popup, anchored in the session view) and resolves it with one of:
 *
 *   GRANT    — give the blocked role the needed access. Two modes:
 *              · Temporary — a bounded (24h), read-only lease (delegation model).
 *              · Permanent — a standing grant. Offered only to founder/admin.
 *              A WRITE wall cannot be granted temporarily (delegation is
 *              read-only) → the server returns 422 and we surface "grant
 *              permanently" only.
 *   CONNECT  — pull a role that ALREADY holds the access into the blocked
 *              session; its grants + credentials apply on the next run.
 *
 * Either action UNBLOCKS the agent (it can retry) and clears the Needs-You item.
 *
 * Status subscription: we poll the request while open. Once it is no longer
 * `open` (resolved elsewhere), the popup closes with a "resolved" toast — the
 * backend is also idempotent (409 already_resolved on a lost race).
 *
 * Mobile = bottom sheet (mentions-inbox pattern + useIsMobile); desktop =
 * centred dialog. ADR-057: monochrome surface, the only accent is the muted
 * warning key glyph. English-only. Gated by VITE_INBOX_V2 at the mount site.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Plus } from 'lucide-react';
import * as React from 'react';

import { ApiError } from '../../lib/api/client';
import { inboxApi, inboxKeys, type AccessRequest } from '../../lib/api/inbox';
import { useIsMobile } from '../../lib/use-media-query';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { PrivatePortal } from '../ui/private-portal';
import { useToaster } from '../ui/toast';

export interface AccessRequestPopupProps {
  /** The access-request to render. When null the popup is dormant. */
  requestId: string | null;
  /**
   * Card the request belongs to, if any — used to refresh its card-scoped inbox
   * state on resolve. Null when opened standalone from the Needs-You lane for a
   * card-less request (the popup renders entirely from getAccessRequest(id)).
   */
  cardId: string | null;
  /** Open state (driven by the `?accessRequest=` consumer). */
  open: boolean;
  /** Close handler — should clear the `?accessRequest=` search param at the call site. */
  onClose: () => void;
}

const STATUS_POLL_MS = 4_000;

export function AccessRequestPopup({ requestId, cardId, open, onClose }: AccessRequestPopupProps) {
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const { toast } = useToaster();

  // The action currently in flight, so we can disable + spinner the right button.
  const [pending, setPending] = React.useState<
    null | 'grant-temporary' | 'grant-permanent' | `connect:${string}`
  >(null);
  const resolvedFiredRef = React.useRef(false);

  const enabled = open && !!requestId;

  const reqQ = useQuery<AccessRequest>({
    queryKey: requestId ? inboxKeys.accessRequest(requestId) : ['inbox', 'access-request', 'idle'],
    queryFn: () => inboxApi.getAccessRequest(requestId as string),
    enabled,
    refetchInterval: enabled ? STATUS_POLL_MS : false,
    staleTime: 0,
  });
  const req = reqQ.data;

  React.useEffect(() => {
    if (open && requestId) {
      setPending(null);
      resolvedFiredRef.current = false;
    }
  }, [open, requestId]);

  const fireResolved = React.useCallback(
    (description: string) => {
      if (resolvedFiredRef.current) return;
      resolvedFiredRef.current = true;
      toast({ title: 'Resolved', description });
      void qc.invalidateQueries({ queryKey: inboxKeys.all });
      // Card-scoped state only exists for a card-backed request.
      if (cardId) void qc.invalidateQueries({ queryKey: inboxKeys.openAsk(cardId) });
      onClose();
    },
    [toast, qc, cardId, onClose],
  );

  // Resolved elsewhere → close with a toast.
  React.useEffect(() => {
    if (!enabled || !req) return;
    if (req.status !== 'open') {
      fireResolved(
        req.resolution === 'granted'
          ? 'Access was granted.'
          : req.resolution === 'connected'
            ? 'A role was connected.'
            : 'This is no longer open.',
      );
    }
  }, [enabled, req, fireResolved]);

  const onDone = React.useCallback(
    (title: string, description: string) => {
      void qc.invalidateQueries({ queryKey: inboxKeys.all });
      if (requestId) void qc.invalidateQueries({ queryKey: inboxKeys.accessRequest(requestId) });
      // Refresh any card-scoped inbox state (raised-hand / open-item badges) —
      // only for a card-backed request; a standalone one has no card query.
      if (cardId) void qc.invalidateQueries({ queryKey: inboxKeys.openAsk(cardId) });
      toast({ title, description });
      onClose();
    },
    [qc, requestId, cardId, toast, onClose],
  );

  const onError = React.useCallback(
    (err: unknown, fallback: string) => {
      setPending(null);
      if (err instanceof ApiError && err.status === 409) {
        fireResolved('This was already resolved.');
        return;
      }
      // A temporary grant on a WRITE wall is refused server-side (422).
      const msg =
        err instanceof ApiError && err.message ? err.message : fallback;
      toast({ title: 'Could not resolve', description: msg, variant: 'destructive' });
    },
    [fireResolved, toast],
  );

  const grantM = useMutation({
    mutationFn: (mode: 'temporary' | 'permanent') =>
      inboxApi.grantAccessRequest(requestId as string, { mode }),
    onSuccess: (r) =>
      onDone(
        r.mode === 'permanent' ? 'Access granted' : 'Temporary access granted',
        'The agent can retry now.',
      ),
    onError: (e) => onError(e, 'Something went wrong granting access.'),
  });

  const connectM = useMutation({
    mutationFn: (roleId: string) =>
      inboxApi.connectAccessRequest(requestId as string, { roleId }),
    onSuccess: () => onDone('Role connected', 'Its access applies to the session on the next run.'),
    onError: (e) => onError(e, 'Something went wrong connecting the role.'),
  });

  if (!enabled) return null;

  const busy = grantM.isPending || connectM.isPending;
  const loading = reqQ.isLoading && !req;
  const holders = req?.holders ?? [];
  const isWrite = req?.axis === 'write';

  const doGrant = (mode: 'temporary' | 'permanent') => {
    if (busy) return;
    setPending(mode === 'temporary' ? 'grant-temporary' : 'grant-permanent');
    grantM.mutate(mode);
  };
  const doConnect = (roleId: string) => {
    if (busy) return;
    setPending(`connect:${roleId}`);
    connectM.mutate(roleId);
  };

  const body = (
    <div className="flex min-h-0 flex-1 flex-col">
      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        </div>
      ) : reqQ.isError ? (
        <p className="px-1 py-6 text-sm text-muted-foreground">
          This request could not be loaded. It may have already been resolved.
        </p>
      ) : (
        <>
          {/* What's blocked */}
          <p className="px-1 text-sm leading-relaxed text-foreground">
            An agent is blocked and needs{' '}
            <code className="rounded bg-foreground/10 px-1 py-0.5 text-[12px] font-medium">
              {req?.needed}
            </code>{' '}
            to {isWrite ? 'write to' : 'access'}{' '}
            <span className="font-medium">
              {req?.resourceName ? `"${req.resourceName}"` : `this ${req?.resourceKind ?? 'resource'}`}
            </span>
            .
          </p>

          {/* GRANT */}
          <div className="mt-5">
            <p className="px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Grant access
            </p>
            <div className={cn('mt-2 flex gap-2', isMobile ? 'flex-col' : 'flex-wrap')}>
              {/* Temporary — hidden for a write wall (delegation is read-only). */}
              {!isWrite && req?.canGrantTemporary && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => doGrant('temporary')}
                  disabled={busy}
                  className={cn('pointer-coarse:min-h-11', isMobile && 'w-full')}
                  data-testid="access-grant-temporary"
                >
                  {pending === 'grant-temporary' ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : null}
                  Grant temporarily (24h)
                </Button>
              )}
              {/* Permanent — founder/admin only. */}
              {req?.canGrantPermanent && (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => doGrant('permanent')}
                  disabled={busy}
                  className={cn('pointer-coarse:min-h-11', isMobile && 'w-full')}
                  data-testid="access-grant-permanent"
                >
                  {pending === 'grant-permanent' ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : null}
                  Grant permanently
                </Button>
              )}
            </div>
            {isWrite && !req?.canGrantPermanent && (
              <p className="mt-2 px-1 text-[12px] leading-snug text-muted-foreground">
                This is a write action — only a permanent grant (founder/admin) can unblock it.
              </p>
            )}
          </div>

          {/* CONNECT */}
          <div className="mt-5">
            <p className="px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Or connect a role that already has it
            </p>
            {holders.length === 0 ? (
              <p className="mt-2 px-1 text-[12px] leading-snug text-muted-foreground">
                No role in this Workspace currently holds that access.
              </p>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                {holders.map((h) => (
                  <Button
                    key={h.roleId}
                    type="button"
                    variant="outline"
                    onClick={() => doConnect(h.roleId)}
                    disabled={busy}
                    className="justify-start pointer-coarse:min-h-11"
                    data-testid={`access-connect-${h.roleSlug}`}
                  >
                    {pending === `connect:${h.roleId}` ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Plus className="mr-2 h-3.5 w-3.5" aria-hidden />
                    )}
                    Connect {h.roleName}
                    {h.canWrite ? (
                      <span className="ml-2 text-[11px] text-muted-foreground">(can write)</span>
                    ) : null}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const header = (
    <div className="flex items-center gap-2">
      <KeyRound className="h-4 w-4 text-warning" aria-hidden />
      <span className="text-sm font-medium text-foreground">An agent needs access</span>
    </div>
  );

  // ── Mobile: bottom sheet ────────────────────────────────────────────
  if (isMobile) {
    return (
      <PrivatePortal>
        <div
            aria-hidden={!open}
            className={cn(
              'fixed inset-0 z-[70] transition-opacity duration-200',
              open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <button
              type="button"
              aria-label="Close access request"
              className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
              onClick={() => onClose()}
            />
            <aside
              role="dialog"
              aria-modal={open}
              aria-label="An agent needs access"
              className={cn(
                'absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col rounded-t-xl border-t border-border bg-popover px-4 pb-[calc(0.5rem+env(safe-area-inset-bottom))] text-popover-foreground shadow-2xl transition-transform duration-300 ease-out',
                open ? 'translate-y-0' : 'translate-y-full',
              )}
            >
              <div className="flex justify-center py-2">
                <div className="h-1 w-10 rounded-full bg-foreground/15" aria-hidden />
              </div>
              <div className="pb-3">{header}</div>
              <div className="min-h-0 flex-1 overflow-y-auto pb-safe">{body}</div>
            </aside>
        </div>
      </PrivatePortal>
    );
  }

  // ── Desktop: centred dialog ─────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{header}</DialogTitle>
          <DialogDescription className="sr-only">
            Grant the blocked agent access, or connect a role that already has it, to let the
            session continue.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
